import React from 'react';
import { useFunnel } from '../../contexts/FunnelContext';
import type { StringKey } from '../../data/i18n';
import type { AccessInfo, FloorLevel, ParkingSpot, TriState, VerticalMethod } from '../../types/funnel';
import { digitsOnly } from '../../utils/format';
import { CarryBandPicker } from './CarryBandPicker';
import { ChoiceCard } from './ChoiceCard';
import { QuantityStepper } from './QuantityStepper';
import { Segmented } from './Segmented';
import { TextField } from './TextField';
const floors: {
  value: FloorLevel;
  key: StringKey;
  flights: number;
}[] = [{
  value: 'ground',
  key: 'flGround',
  flights: 0
}, {
  value: 'second',
  key: 'flSecond',
  flights: 1
}, {
  value: 'third',
  key: 'flThird',
  flights: 2
}, {
  value: 'fourth_plus',
  key: 'flFourth',
  flights: 3
}];
const parkingOptions: {
  value: ParkingSpot;
  key: StringKey;
}[] = [{
  value: 'driveway',
  key: 'pkDriveway'
}, {
  value: 'street',
  key: 'pkStreet'
}, {
  value: 'lot',
  key: 'pkLot'
}, {
  value: 'loading_dock',
  key: 'pkDock'
}, {
  value: 'unsure',
  key: 'pkUnsure'
}];
interface AccessEditorProps {
  title: string;
  access: AccessInfo;
  onChange: (patch: Partial<AccessInfo>) => void;
  multiUnit: boolean;
}
export function AccessEditor({
  title,
  access,
  onChange,
  multiUnit
}: AccessEditorProps) {
  const {
    t
  } = useFunnel();
  const tri: {
    value: TriState;
    label: string;
  }[] = [{
    value: 'yes',
    label: t('yes')
  }, {
    value: 'no',
    label: t('no')
  }, {
    value: 'unknown',
    label: t('unsure')
  }];
  const upstairs = access.floor !== 'ground';
  const elevator = upstairs && access.method !== 'stairs';
  return <section aria-label={title} className="space-y-6" data-id="element-356" data-mp="uouqzklep7rt">
      <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-357" data-mp="zmw9o4ex752r">{title}</h2>

      <fieldset data-id="element-358" data-mp="zktllpxbvm0o">
        <legend className="mb-2 text-sm font-semibold text-ink" data-id="element-359" data-mp="2wf6b3dgk3esy">{t('floorLabel')}</legend>
        <div role="radiogroup" aria-label={t('floorLabel')} className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-id="element-360" data-mp="2whj0ucsk2tgk">
          {floors.map(f => <ChoiceCard key={f.value} size="sm" selected={access.floor === f.value} onSelect={() => onChange({
          floor: f.value,
          flights: f.flights,
          method: f.value === 'ground' ? 'stairs' : access.method
        })} title={t(f.key)} data-id="element-361" data-mp="2temmuw9jivky" />)}
        </div>
      </fieldset>

      {upstairs && <Segmented<VerticalMethod> label={t('methodLabel')} value={access.method} onChange={v => onChange({
      method: v
    })} options={[{
      value: 'stairs',
      label: t('mStairs')
    }, {
      value: 'passenger_elevator',
      label: t('mPassenger')
    }, {
      value: 'freight_elevator',
      label: t('mFreight')
    }]} data-id="element-362" data-mp="3cc7do8vv8487" />}

      {upstairs && access.method === 'stairs' && <div className="max-w-sm rounded-lg bg-ink-100" data-id="element-363" data-mp="3w0y6rsv0ncly">
          <QuantityStepper label={t('flightsLabel')} value={access.flights} onChange={n => onChange({
        flights: n
      })} decLabel={t('decrease')} incLabel={t('increase')} max={20} data-id="element-364" data-mp="3r1vd14khksta" />
        </div>}

      {elevator && <div className="grid gap-5 sm:grid-cols-2" data-id="element-365" data-mp="iib5cx981ud2">
          <Segmented<TriState> label={t('elevResLabel')} value={access.elevatorReservation} onChange={v => onChange({
        elevatorReservation: v
      })} options={tri} data-id="element-366" data-mp="4jjz1x0lpczh" />
          <Segmented<TriState> label={t('sharedElevLabel')} value={access.sharedElevator} onChange={v => onChange({
        sharedElevator: v
      })} options={tri} data-id="element-367" data-mp="9h3bcrdqnfos" />
        </div>}

      <fieldset data-id="element-368" data-mp="3vqweo7lu5ida">
        <legend className="mb-2 text-sm font-semibold text-ink" data-id="element-369" data-mp="3vrflot9y6mcj">{t('parkingLabel')}</legend>
        <div role="radiogroup" aria-label={t('parkingLabel')} className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-id="element-370" data-mp="3hzr9kpzp7xvk">
          {parkingOptions.map(p => <ChoiceCard key={p.value} size="sm" selected={access.parking === p.value} onSelect={() => onChange({
          parking: p.value
        })} title={t(p.key)} data-id="element-371" data-mp="37zs637i4yxpk" />)}
        </div>
      </fieldset>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-372" data-mp="k5ehds6bxzlf">
        <Segmented<TriState> label={t('truckNearLabel')} value={access.truckNearEntrance} onChange={v => onChange({
        truckNearEntrance: v
      })} options={tri} data-id="element-373" data-mp="k43d7r8z2pts" />
        <Segmented<TriState> label={t('gateLabel')} value={access.gateAccess} onChange={v => onChange({
        gateAccess: v
      })} options={tri} data-id="element-374" data-mp="aebhgtp4zqnq" />
      </div>

      {(multiUnit || elevator) && <Segmented<TriState> label={t('coiLabel')} value={access.coiRequired} onChange={v => onChange({
      coiRequired: v
    })} options={tri} data-id="element-375" data-mp="gnpgbix0x99" />}

      <CarryBandPicker label={t('carryLabel')} value={access.carryBand} onChange={v => onChange({
      carryBand: v
    })} data-id="element-376" data-mp="3sjwsy3vhbx9s" />
      <TextField label={t('feetLabel')} inputMode="numeric" value={access.carryFeet} onChange={v => onChange({
      carryFeet: digitsOnly(v).slice(0, 4)
    })} className="max-w-[200px]" data-id="element-377" data-mp="3nlvbfndpc60f" />

      {(multiUnit || upstairs) && <CarryBandPicker label={t('innerCarryLabel')} value={access.innerCarryBand ?? 'unsure'} onChange={v => onChange({
      innerCarryBand: v
    })} data-id="element-378" data-mp="1gv5piu7jnl9p" />}

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-379" data-mp="1odwqksg45fah">
        <TextField label={t('restrictionsLabel')} value={access.restrictions ?? ''} onChange={v => onChange({
        restrictions: v.slice(0, 200)
      })} data-id="element-380" data-mp="1tdht1cuhs5ry" />
        <TextField label={t('loadingHoursLabel')} value={access.loadingHours ?? ''} onChange={v => onChange({
        loadingHours: v.slice(0, 120)
      })} data-id="element-381" data-mp="1y5svamcpg3no" />
      </div>
      <TextField label={t('accessNotesLabel')} value={access.notes} onChange={v => onChange({
      notes: v
    })} data-id="element-382" data-mp="15g2eu6nlopoe" />
    </section>;
}